import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box, TableCell } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { LanguageUtils, type QuestionAnswer } from '@pbt/pbt-ui-components'
import HtmlContentRenderer from '@pbt/pbt-ui-components/src/components/HtmlContentRenderer'

import DragAndDropTable, {
  type DragAndDropTableProps,
} from '~/components/common/lists/DragAndDropTable'
import Typography from '~/components/elements/Typography/Typography'
import FeatureToggle from '~/constants/featureToggle'
import {
  getAnswerInputTypes,
  getFeatureToggle,
} from '~/store/reducers/constants'

const useStyles = makeStyles(
  (theme) => ({
    root: {
      paddingLeft: theme.spacing(4.5),
    },
    tableCell: {
      height: 54,
    },
    tableTitle: {
      height: 38,
    },
  }),
  { name: 'AnswersTable' },
)

const getId = (answer: QuestionAnswer) => answer.id || answer.text

export interface AnswersTableProps
  extends Omit<DragAndDropTableProps<QuestionAnswer>, 'children'> {
  answers: QuestionAnswer[]
}

const AnswersTable = ({ answers, ...rest }: AnswersTableProps) => {
  const classes = useStyles()
  const AnswerInputTypes = useSelector(getAnswerInputTypes)
  const { t } = useTranslation('Common')

  const isFollowUpQuestionsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOLLOW_UP_QUESTIONS),
  )

  const headerRow = [{ label: t('Common:INCLUDE_NOUN') }]

  const mainColumns = [
    {
      label: t('Common:ANSWER_ONE'),
      prop: 'text' as keyof QuestionAnswer,
    },
  ]

  const renderIncludeCell = (answer: QuestionAnswer) => {
    if (isFollowUpQuestionsEnabled && answer.followUpQuestion?.followUp) {
      const { questionText, questionSubText } = answer.followUpQuestion

      return (
        <Box py={1}>
          {questionText && (
            <Box py={1}>
              <Typography.ParagraphSm color="grayGray2">
                Text:
              </Typography.ParagraphSm>
              <HtmlContentRenderer content={questionText} />
            </Box>
          )}
          {questionSubText && (
            <Box py={1}>
              <Typography.ParagraphSm color="grayGray2">
                Secondary copy:
              </Typography.ParagraphSm>
              <HtmlContentRenderer content={questionSubText} />
            </Box>
          )}
          {!questionText && !questionSubText && '-'}
        </Box>
      )
    }

    if (answer.inputs.length > 0) {
      return answer.inputs
        .map((input) =>
          LanguageUtils.getConstantTranslatedName(
            input.inputTypeId,
            AnswerInputTypes,
            '',
          ).toLowerCase(),
        )
        .join(', ')
    }

    return '-'
  }

  return (
    <DragAndDropTable
      useIndexAsId
      classes={classes}
      data={answers}
      getId={getId}
      headerRow={headerRow}
      mainColumns={mainColumns}
      {...rest}
    >
      {({ item: answer, tableCellClassName }) => (
        <TableCell
          className={classNames(classes.tableCell, tableCellClassName)}
        >
          {renderIncludeCell(answer)}
        </TableCell>
      )}
    </DragAndDropTable>
  )
}

export default AnswersTable
